import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { LibraryLookupToolResponse, PatronLookupService } from '../patron-lookup.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { SharedService } from '../../../services/shared/shared.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { StorageService } from '../../../services/storage/storage.service';
import { Library } from '../../../core/interfaces/library.interface';

@Component({
  selector: 'app-patron-get-list-info',
  templateUrl: './patron-get-list-info.component.html',
  styleUrls: ['./patron-get-list-info.component.scss'],
  providers: [{ provide: ANIMATION_MODULE_TYPE, useValue: 'BrowserAnimations' }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PatronGetListInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('libdropdown') libdropdown: ElementRef;
  @ViewChild('libdropdown2') libdropdown2: ElementRef;

  cid: any;
  showInfo = true;
  loading = true;
  filterLibrary: UntypedFormControl = new UntypedFormControl();
  intermediateForm: UntypedFormGroup;
  libraries: Library[] = [];
  library = '';
  interDiv: boolean;
  ultimateDiv: boolean;
  ultimateForm: UntypedFormGroup;
  btnLoading: boolean;
  userData: any;
  PatronCheck: any;
  instructionalText1 = "Please select patron's library and enter patron's barcode";
  libraryDropText1 = 'Type your library name or library code';
  libraryDropText2 = 'Patron library';
  userNameText1 = "Patron's barcode";
  userNameText2 = 'Patron barcode';
  interimText1 = "Please enter patron's PIN/password.";
  interimText2 = 'Patron PIN/password';
  closeButtonText1 = 'Close';
  closeButtonText2 = 'Check another patron';
  patronStatusText1 = 'Patron status: Okay';
  patronStatusText2 = 'Patron status: Blocked';

  private _lid: any;
  private _lpk: any;
  private _interObj: any;
  private _filteredLibraries = new BehaviorSubject<Library[]>(null);
  private _libs: Library[] = [];
  private _infoResponse: LibraryLookupToolResponse;
  private _destroyed$ = new Subject<void>();

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _fb: UntypedFormBuilder,
    private _patronLookupService: PatronLookupService,
    private _sharedService: SharedService,
    private _router: Router,
    private _storageService: StorageService,
  ) {
    this.interDiv = true;
    this.ultimateDiv = false;
    this.btnLoading = false;
  }

  ngOnInit() {
    this._activatedRoute.params.subscribe((params: Params) => {
      this.cid = params['cid'];
      this._lid = params['lid'];
      this.userData = this._getUserData();
      this._buildIntermediateForm();
      this._buildUltimateForm();

      this._sharedService.userData$.pipe(takeUntil(this._destroyed$)).subscribe((token) => {
        if (token != null) {
          this._getInfo();
        } else {
          if (this.cid && this._lid) {
            this._router.navigate(['/PatronLookupTool'], {
              queryParams: { cid: this.cid, lid: this._lid },
            });
          } else if (!this.cid || !this._lid) {
            this._router.navigateByUrl('/PatronLookupTool/404');
          }
        }
      });
      this.filterLibrary.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
        // listen for search field value changes
        this.filterLibs(value);
      });
      this._filteredLibraries.subscribe((value) => {
        // listen to change the library list on search filter.
        this.libraries = value;
      });
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this._focusOndrop();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.PatronCheck = undefined;
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onLogout() {
    this._router.navigate(['/PatronLookupTool'], {
      queryParams: { cid: this.cid, lid: this._lid },
    });
  }

  showLibList(): void {
    this.library = '';
  }

  firstCancel(): void {
    this._router.navigate(['/PatronLookupTool'], {
      queryParams: { cid: this.cid, lid: this._lid },
    });
  }

  secondCancel(): void {
    this.interDiv = true;
    this.ultimateDiv = false;
    this.PatronCheck = false;
  }

  close(): void {
    this._resetIntermediateForm();
    this._resetUlitmateForm();
    if (this.userData) {
      let userType = 0;
      if (this.userData.isStaff) {
        userType = 1;
      }
      if (this.userData.isPatron) {
        userType = 2;
      }
      if (this.userData.isGuest) {
        userType = 3;
      }
      if (userType == 1) {
        this.secondCancel();
      } else {
        this.firstCancel();
      }
    } else {
      this.firstCancel();
    }
    setTimeout(() => {
      this._focusOnDropdown();
    }, 1500);
  }

  ultimateSubmit(): void {
    this.btnLoading = true;
    const postBody = {
      LPK: this._interObj.LPK,
      Username: this._interObj.Username,
      Password: this.ultimateForm.value.password,
    };
    this._patronLookupService.postCheck(postBody).subscribe((response) => {
      this.btnLoading = false;
      if (response.status == '200 OK') {
        this.PatronCheck = response.data;
        this.interDiv = false;
        this.ultimateDiv = false;
      } else {
        this._sharedService.openSnackBar(response.message, 'ok');
      }
    });
  }

  interSubmit(): void {
    if (/\S/.test(this.intermediateForm.value.user)) {
      this.btnLoading = true;
      let lpk: any;
      // find lpk of the selected library and save it in a BS
      let keepGoing = true;
      const dummyArray = [];
      const libraryName = this.filterLibrary.value;
      for (let index = 0; index < libraryName.length; index++) {
        if (libraryName.charAt(index) == ':') {
          keepGoing = false;
        }
        if (keepGoing) {
          dummyArray.push(libraryName.charAt(index));
        }
      }
      const newlib = dummyArray.join('');
      const array = this._infoResponse.libraries.slice();
      this.btnLoading = false;
      for (let index = 0; index < array.length; index++) {
        const element = array[index];
        if (element.lid.toUpperCase() == newlib.toUpperCase()) {
          this._lpk = element.lpk;
        }
      }
      const obj = { LPK: this._lpk, Username: this.intermediateForm.value.user };
      if (obj.LPK != '' && obj.Username != '') {
        this._patronLookupService.setIntermediateBS(obj);
        this._interObj = obj;
        // show pass word div, put a go back button as well to swap divs
        this.interDiv = false;
        this.ultimateDiv = true;
      }
      setTimeout(() => {
        this._focusOnDropdown();
      }, 1500);
    } else {
      this.btnLoading = false;
      const message = 'please enter a barcode';
      this._sharedService.openSnackBar(message, 'ok');
    }
  }

  private _focusOnDropdown(): void {
    if (this.libdropdown2) {
      this.libdropdown2.nativeElement.focus();
    }
  }

  filterLibs(value): void {
    const dummyArray: any = [];
    for (let index = 0; index < this._libs.length; index++) {
      const element = this._libs[index];
      if (element.lid.toUpperCase().includes(value.toUpperCase())) {
        dummyArray.push(element);
      } else if (element.name.toUpperCase().includes(value.toUpperCase())) {
        dummyArray.push(element);
      }
    }
    this._filteredLibraries.next(dummyArray);
  }

  private _focusOndrop(): void {
    if (this.libdropdown) {
      this.libdropdown.nativeElement.focus();
    }
  }

  private _resetUlitmateForm(): void {
    if (this.ultimateForm) {
      this.ultimateForm.reset();
    }
  }

  private _getUserData() {
    return this._storageService.getUserData();
  }

  private _resetIntermediateForm(): void {
    this.library = '';
    if (this.intermediateForm) {
      this.intermediateForm.reset();
    }
  }

  private _buildIntermediateForm(): void {
    this.intermediateForm = this._fb.group({
      user: [''],
    });
  }

  private _buildUltimateForm(): void {
    this.ultimateForm = this._fb.group({
      password: [''],
    });
  }

  private _getInfo(): void {
    this._patronLookupService.getInfo().subscribe(
      (response) => {
        if (response.status == '200 OK') {
          this._infoResponse = response.data;
          if (this._infoResponse.active) {
            this.showInfo = true;
            this.loading = false;
            this.libraries = this._infoResponse.libraries;
            this._libs = this._infoResponse.libraries;
          } else {
            const message = response.data.errorMessage;
            this._sharedService.openSnackBar(message, 'ok');
            this.showInfo = false;
            this.loading = false;
          }
        } else {
          this._sharedService.openSnackBar(response.message, 'ok');
          this.showInfo = false;
          this.loading = false;
        }
      },
      (error: unknown) => {
        this.loading = false;
      },
    );
  }
}
